import type { JourneyProgress, JourneyStep, LearningMode, TutorialContent } from './types'

export interface ProgressStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

export function progressStorageKey(content: TutorialContent): string {
  return `badreor.ptw-learning.v2:${encodeURIComponent(content.id)}:${encodeURIComponent(content.version)}`
}

export function getRoleSteps(content: TutorialContent, roleCode: string): JourneyStep[] {
  const role = content.roles.find((item) => item.code === roleCode)
  if (roleCode === 'ALL' || !role) return content.journey.steps
  const allowed = new Set(role.stepIds)
  return content.journey.steps.filter((step) => allowed.has(step.id))
}

export function createInitialProgress(content: TutorialContent): JourneyProgress {
  return {
    schemaVersion: 2,
    contentId: content.id,
    contentVersion: content.version,
    mode: 'journey',
    selectedRole: 'ALL',
    currentStepId: content.journey.steps[0]?.id ?? '',
    readStepIds: [],
    practisedStepIds: [],
    hasStarted: false,
  }
}

export function normalizeProgress(content: TutorialContent, value: unknown): JourneyProgress {
  const initial = createInitialProgress(content)
  if (typeof value !== 'object' || value === null) return initial
  const saved = value as Partial<JourneyProgress>
  if (saved.schemaVersion !== 2 || saved.contentId !== content.id || saved.contentVersion !== content.version) return initial
  const selectedRole = content.roles.some((role) => role.code === saved.selectedRole) ? saved.selectedRole! : 'ALL'
  const path = getRoleSteps(content, selectedRole)
  const ids = new Set(content.journey.steps.map((step) => step.id))
  const known = (values: unknown): string[] => Array.isArray(values)
    ? [...new Set(values.filter((id): id is string => typeof id === 'string' && ids.has(id)))]
    : []
  const modes: LearningMode[] = ['home', 'journey', 'roles', 'help']
  return {
    ...initial,
    mode: modes.includes(saved.mode as LearningMode) ? saved.mode! : 'journey',
    selectedRole,
    currentStepId: path.some((step) => step.id === saved.currentStepId)
      ? saved.currentStepId! : path[0]?.id ?? content.journey.steps[0]?.id ?? '',
    readStepIds: known(saved.readStepIds),
    practisedStepIds: known(saved.practisedStepIds),
    hasStarted: saved.hasStarted === true,
  }
}

function browserStorage(): ProgressStorage | null {
  return typeof window === 'undefined' ? null : window.localStorage
}

export function loadProgress(content: TutorialContent, storage?: ProgressStorage | null): {
  progress: JourneyProgress
  storageUnavailable: boolean
} {
  try {
    const target = storage === undefined ? browserStorage() : storage
    if (!target) return { progress: createInitialProgress(content), storageUnavailable: true }
    const raw = target.getItem(progressStorageKey(content))
    return {
      progress: normalizeProgress(content, raw === null ? null : JSON.parse(raw)),
      storageUnavailable: false,
    }
  } catch {
    return { progress: createInitialProgress(content), storageUnavailable: true }
  }
}

/** Browser-local self-study only: never an identity-verified training/permit record. */
export function saveProgress(content: TutorialContent, progress: JourneyProgress, storage?: ProgressStorage | null): boolean {
  try {
    const target = storage === undefined ? browserStorage() : storage
    if (!target) return false
    target.setItem(progressStorageKey(content), JSON.stringify(normalizeProgress(content, progress)))
    return true
  } catch {
    return false
  }
}
